class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

/* -------------------------
   SHARED COUNT UPDATER (global)
   Ensures the cart item number updates instantly everywhere,
   even before/without a sections refresh.
------------------------- */
if (!window.__updateCartCountsFromCart) {
  window.__updateCartCountsFromCart = async function () {
    try {
      const cart = await fetch(`${routes.cart_url}.js?ts=${Date.now()}`, {
        headers: { Accept: "application/json", "Cache-Control": "no-store" },
      }).then((r) => r.json());

      const count = Number(cart.item_count || 0);

      // Update common count targets
      const targets = document.querySelectorAll(
        "[data-cart-count], .cart-count, .cart-count-bubble, #cart-count, .header__cart-count"
      );
      targets.forEach((el) => {
        // Attribute many themes read from
        if (el.hasAttribute("data-cart-count")) {
          el.setAttribute("data-cart-count", String(count));
        }
        // Visible text (fallback)
        if ("textContent" in el) el.textContent = String(count);

        // Toggle visibility if theme uses bubbles
        if (el.classList.contains("hidden")) {
          el.classList.toggle("hidden", count === 0);
        }
        if (el.hasAttribute("aria-hidden")) {
          el.setAttribute("aria-hidden", count === 0 ? "true" : "false");
        }
      });
    } catch (e) {
      console.error("Count sync failed:", e);
    }
  };
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));

    // Bind upsell toggle listener only once, and only from the Drawer instance
    const cartUpsellToggle = document.getElementById("cart-upsell-toggle");
    const isDrawerInstance = this.tagName === "CART-DRAWER-ITEMS";
    if (cartUpsellToggle && isDrawerInstance && !cartUpsellToggle.dataset.bound) {
      cartUpsellToggle.addEventListener(
        "change",
        this.onCartUpsellToggle.bind(this)
      );
      cartUpsellToggle.dataset.bound = "1";
    }

    // guards for upsell toggle concurrency (local)
    this.addingUpsellProduct = false;
    this.removingUpsellProduct = false;

    // guards for upsell toggle concurrency (global across multiple <cart-items>)
    if (!window.__UPS_SESS_UPSELL_ADDING) window.__UPS_SESS_UPSELL_ADDING = false;
    if (!window.__UPS_SESS_UPSELL_REMOVING) window.__UPS_SESS_UPSELL_REMOVING = false;
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
    if (this.tagName !== "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}.js?ts=${Date.now()}`)
        .then((response) => response.json())
        .then((parsedState) => {
          this.updateCartUpsellToggleState();
          this.updateCartUpsellVisibility(parsedState.item_count);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute("value");
    }
    this.isEnterPressed = false;
  }

  async updateCartIconBubble() {
    try {
      const res = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble&ts=${Date.now()}`, {
        headers: { "Cache-Control": "no-store" },
      });
      const html = new DOMParser().parseFromString(await res.text(), "text/html");
      const target = document.getElementById("cart-icon-bubble");
      const source = html.getElementById("cart-icon-bubble");
      if (target && source) target.replaceWith(source);
    } catch (e) {
      console.error(e);
    } finally {
      // Hard-sync counts immediately as well (prevents any lag)
      window.__updateCartCountsFromCart();
    }
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    // Upsell hard cap = 1. Clamp before validations & submission.
    const upsellScriptTag = document.querySelector("script[data-cart-upsell-variant-id]");
    const upsellVariantId = upsellScriptTag ? upsellScriptTag.dataset.cartUpsellVariantId : "";
    const thisVariantId = event.target.dataset.quantityVariantId;
    const cappedValue = (thisVariantId && upsellVariantId && thisVariantId === upsellVariantId)
      ? Math.min(1, inputValue)
      : inputValue;

    if (cappedValue !== inputValue) {
      event.target.value = String(cappedValue);
    }

    if (cappedValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        "[min]",
        event.target.dataset.min
      );
    } else if (cappedValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        "[max]",
        event.target.max
      );
    } else if (cappedValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        "[step]",
        event.target.step
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        cappedValue,
        document.activeElement && document.activeElement.getAttribute("name"),
        thisVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  // Keep header bubble in sync at the end of both branches
  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer&ts=${Date.now()}`, {
        headers: { "Cache-Control": "no-store" },
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          const parsedStateElement = html.querySelector("[data-cart-drawer-state]");
          const parsedState = parsedStateElement
            ? JSON.parse(parsedStateElement.textContent)
            : null;

          this.updateCartUpsellToggleState();
          if (parsedState) {
            this.updateCartUpsellVisibility(parsedState.item_count);
          }

          this.updateCartIconBubble();
        })
        .catch((e) => console.error(e));
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items&ts=${Date.now()}`, {
        headers: { "Cache-Control": "no-store" },
      })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const sourceQty = html.querySelector("cart-items");
          if (sourceQty) this.innerHTML = sourceQty.innerHTML;

          const parsedStateElement = html.querySelector("[data-cart-state]");
          const parsedState = parsedStateElement
            ? JSON.parse(parsedStateElement.textContent)
            : null;

          this.updateCartUpsellToggleState();
          if (parsedState) {
            this.updateCartUpsellVisibility(parsedState.item_count);
          }

          this.updateCartIconBubble();
        })
        .catch((e) => console.error(e));
    }
  }

  // Force-refresh the currently visible cart UI NOW (no pubsub delay)
  async refreshCartUIImmediate() {
    try {
      if (this.tagName === "CART-DRAWER-ITEMS" || document.querySelector("cart-drawer")) {
        const res = await fetch(`${routes.cart_url}?section_id=cart-drawer&ts=${Date.now()}`, {
          headers: { "Cache-Control": "no-store" },
        });
        const html = new DOMParser().parseFromString(await res.text(), "text/html");
        const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) targetElement.replaceWith(sourceElement);
        }
      } else {
        const res = await fetch(`${routes.cart_url}?section_id=main-cart-items&ts=${Date.now()}`, {
          headers: { "Cache-Control": "no-store" },
        });
        const html = new DOMParser().parseFromString(await res.text(), "text/html");
        const sourceQty = html.querySelector("cart-items");
        if (sourceQty) this.innerHTML = sourceQty.innerHTML;
      }
      // After DOM swaps, force-update counts instantly
      await window.__updateCartCountsFromCart();
      // Also refresh bubble section (kept for parity)
      await this.updateCartIconBubble();
    } catch (e) {
      console.error(e);
    }
  }

  updateCartUpsellToggleState() {
    const cartUpsellToggle = document.getElementById("cart-upsell-toggle");
    const scriptTag = document.querySelector(
      "script[data-cart-upsell-variant-id]"
    );
    const cartUpsellVariantId = scriptTag
      ? scriptTag.dataset.cartUpsellVariantId
      : "";
    const cartItems = document.querySelectorAll(".cart-item");

    const upsellItem = Array.from(cartItems).find((item) => {
      const input = item.querySelector("input[data-quantity-variant-id]");
      return (
        input &&
        input.getAttribute("data-quantity-variant-id") === cartUpsellVariantId
      );
    });

    if (cartUpsellToggle && cartUpsellToggle.checked !== !!upsellItem) {
      cartUpsellToggle.checked = !!upsellItem;
    }
  }

  updateCartUpsellVisibility(itemCount) {
    const cartUpsellContainer = document.querySelector(
      ".cart-upsell-toggle-container"
    );
    if (cartUpsellContainer) {
      if (itemCount === 0) {
        cartUpsellContainer.classList.add("hidden");
      } else {
        cartUpsellContainer.classList.remove("hidden");
      }
    }
  }

  async onCartUpsellToggle(event) {
    const scriptTag = document.querySelector(
      "script[data-cart-upsell-variant-id]"
    );
    const cartUpsellVariantId = scriptTag
      ? scriptTag.dataset.cartUpsellVariantId
      : "";
    const isChecked = event.target.checked;

    // Global cross-instance guards
    if (window.__UPS_SESS_UPSELL_ADDING || window.__UPS_SESS_UPSELL_REMOVING) {
      event.target.checked = !isChecked;
      return;
    }

    if (isChecked) {
      if (this.addingUpsellProduct) return;
      this.addingUpsellProduct = true;
      window.__UPS_SESS_UPSELL_ADDING = true;

      // Suppress Auto-Bonus from double-adding during this user-initiated add
      window.__UPS_SESS_SUPPRESS_AUTOBONUS_ADD = true;

      try {
        // Ensure it's not already present (first-toggle double-add protection)
        const alreadyPresent = await this.cartHasVariant(cartUpsellVariantId);
        if (!alreadyPresent) {
          await this.addUpsellProduct(cartUpsellVariantId);
        } else {
          await this.refreshCartUIImmediate();
          this.updateCartUpsellToggleState();
        }

        // Ensure counts are instant
        await window.__updateCartCountsFromCart();

        // Update progress bar
        const parsedState = await fetch(`${routes.cart_url}.js?ts=${Date.now()}`).then((r) =>
          r.json()
        );
        this.updateProgressBar({
          total_price: parsedState.total_price,
          items_subtotal_price: parsedState.items_subtotal_price,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setTimeout(() => {
          window.__UPS_SESS_SUPPRESS_AUTOBONUS_ADD = false;
        }, 300);
        this.addingUpsellProduct = false;
        window.__UPS_SESS_UPSELL_ADDING = false;
      }
    } else {
      if (this.removingUpsellProduct) return;
      this.removingUpsellProduct = true;
      window.__UPS_SESS_UPSELL_REMOVING = true;

      try {
        await this.removeUpsellProduct(cartUpsellVariantId);

        // Ensure counts are instant
        await window.__updateCartCountsFromCart();

        const parsedState = await fetch(`${routes.cart_url}.js?ts=${Date.now()}`).then((r) =>
          r.json()
        );
        this.updateProgressBar({
          total_price: parsedState.total_price,
          items_subtotal_price: parsedState.items_subtotal_price,
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.removingUpsellProduct = false;
        window.__UPS_SESS_UPSELL_REMOVING = false;
      }
    }
  }

  async cartHasVariant(variantId) {
    try {
      const cart = await fetch(`${routes.cart_url}.js?ts=${Date.now()}`, {
        headers: { Accept: "application/json" },
      }).then((r) => r.json());
      return cart.items.some((it) => String(it.id) === String(variantId));
    } catch {
      return false;
    }
  }

  // ✅ Use /cart/add.js to avoid navigation
  async addUpsellProduct(cartUpsellVariantId) {
    const payload = { id: cartUpsellVariantId, quantity: 1 };

    const response = await fetch(`${routes.cart_add_url}.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to add upsell product:", errorText);
      throw new Error("Failed to add upsell product");
    }

    // Immediately refresh the visible cart UI (no delay)
    await this.refreshCartUIImmediate();

    // Counts: hard-sync instantly
    await window.__updateCartCountsFromCart();

    // Notify others (still publish for listeners)
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "cart-items-upsell",
      cartData: null,
    });

    // ensure toggle reflects state
    this.updateCartUpsellToggleState();
  }

  // Remove upsell by KEY (stable), not by line index (no navigation)
  async removeUpsellProduct(cartUpsellVariantId) {
    try {
      // 1) Find the exact line key for the upsell from the live cart
      const cart = await fetch(`${routes.cart_url}.js?ts=${Date.now()}`, {
        headers: { Accept: "application/json" },
      }).then((r) => r.json());

      const upsellLine = cart.items.find(
        (it) => String(it.id) === String(cartUpsellVariantId)
      );
      if (!upsellLine) {
        console.warn("Upsell product not found in the cart.");
        return;
      }

      // 2) Remove by key (not by line number); keep sections refresh path
      const body = JSON.stringify({
        id: upsellLine.key,
        quantity: 0,
        sections: this.getSectionsToRender().map((s) => s.section),
        sections_url: window.location.pathname,
      });

      const stateText = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        ...{ body },
      }).then((r) => r.text());

      const parsedState = JSON.parse(stateText);

      // Mirror updateQuantity's DOM refresh
      this.classList.toggle("is-empty", parsedState.item_count === 0);
      const cartDrawerWrapper = document.querySelector("cart-drawer");
      const cartFooter = document.getElementById("main-cart-footer");
      if (cartFooter)
        cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
      if (cartDrawerWrapper)
        cartDrawerWrapper.classList.toggle(
          "is-empty",
          parsedState.item_count === 0
        );

      this.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) ||
          document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });

      this.updateProgressBar({
        total_price: parsedState.total_price,
        items_subtotal_price: parsedState.items_subtotal_price,
      });

      // keep header count in sync (instantly)
      await window.__updateCartCountsFromCart();
      await this.updateCartIconBubble();

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "cart-items",
        cartData: parsedState,
        variantId: cartUpsellVariantId,
      });

      this.updateCartUpsellToggleState();
      this.updateCartUpsellVisibility(parsedState.item_count);
    } catch (error) {
      console.error("Error removing upsell product:", error);
    } finally {
      this.removingUpsellProduct = false;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  // Return the fetch Promise so callers can await
  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    // Enforce upsell max quantity = 1
    const scriptTag = document.querySelector("script[data-cart-upsell-variant-id]");
    const upsellVariantId = scriptTag ? scriptTag.dataset.cartUpsellVariantId : "";
    if (variantId && upsellVariantId && String(variantId) === String(upsellVariantId)) {
      quantity = Math.min(1, parseInt(quantity, 10) || 0);
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then(async (state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute("value");
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          quantityElement &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          message =
            typeof updatedValue === "undefined"
              ? window.cartStrings.error
              : window.cartStrings.quantityError.replace(
                  "[quantity]",
                  updatedValue
                );
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          const cartDrawerWrapper2 = document.querySelector("cart-drawer");
          cartDrawerWrapper2
            ? trapFocus(
                cartDrawerWrapper2,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (
          parsedState.item_count === 0 &&
          document.querySelector("cart-drawer")
        ) {
          const cartDrawerWrapper3 = document.querySelector("cart-drawer");
          trapFocus(
            cartDrawerWrapper3.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper3.querySelector("a")
          );
        } else if (
          document.querySelector(".cart-item") &&
          document.querySelector("cart-drawer")
        ) {
          const cartDrawerWrapper4 = document.querySelector("cart-drawer");
          trapFocus(cartDrawerWrapper4, document.querySelector(".cart-item__name"));
        }

        this.updateProgressBar({
          total_price: parsedState.total_price,
          items_subtotal_price: parsedState.items_subtotal_price,
        });

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });

        this.updateCartUpsellToggleState();
        this.updateCartUpsellVisibility(parsedState.item_count);

        // keep header count in sync for quantity changes too
        await window.__updateCartCountsFromCart();
        this.updateCartIconBubble();
      })
      .catch(() => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateProgressBar({ total_price, items_subtotal_price }) {
    const progressWrapper = document.getElementById("cart-progress-wrapper");
    if (!progressWrapper) return;

    const cartTotalCents =
      progressWrapper.dataset.useItemsSubtotal === "true"
        ? items_subtotal_price
        : total_price;

    const currencyFormat = progressWrapper.dataset.currencyFormat;
    const thresholds = progressWrapper.dataset.thresholds
      .split(",")
      .map(Number);
    const preGoalMessages = progressWrapper.dataset.preGoalMessages.split("||");
    const postGoalMessages =
      progressWrapper.dataset.postGoalMessages.split("||");
    const goalPositions = progressWrapper.dataset.goalPositions
      .split(",")
      .map(Number);

    const totalThreshold = thresholds[thresholds.length - 1];
    const progressPercentage = Math.min(
      (cartTotalCents / totalThreshold) * 100,
      100
    );

    const progressBar = document.getElementById("cart-progress-bar");
    const goalIcons = document.querySelectorAll(".goal-icon");
    const goalMessageElement = document.querySelector(".goal-message");

    if (cartTotalCents === 0) {
      progressWrapper.style.display = "none";
      if (goalMessageElement) goalMessageElement.style.display = "none";
      if (progressBar) progressBar.style.width = "0%";
    } else {
      progressWrapper.style.display = "block";
      if (progressBar) progressBar.style.width = `${progressPercentage}%`;

      if (progressPercentage >= 100) {
        progressWrapper.classList.add("full");
      } else {
        progressWrapper.classList.remove("full");
      }

      let nextGoalIndex = -1;
      for (let i = 0; i < thresholds.length; i++) {
        if (cartTotalCents < thresholds[i]) {
          nextGoalIndex = i;
          break;
        }
      }

      goalIcons.forEach((goalIcon, index) => {
        const cartTotalDiff = cartTotalCents - thresholds[index];
        const icon = goalIcon.querySelector("img");
        const goalNumber = goalIcon.dataset.index;

        if (icon) {
          if (cartTotalDiff < 0) {
            const regularIconUrl = goalIcon.dataset.regularIcon;
            if (regularIconUrl) {
              icon.src = regularIconUrl;
              icon.srcset = `${regularIconUrl} 50w`;
              icon.alt = `Goal ${goalNumber}`;
            }
          } else {
            const reachedIconUrl = goalIcon.dataset.reachedIcon;
            if (reachedIconUrl) {
              icon.src = reachedIconUrl;
              icon.srcset = `${reachedIconUrl} 50w`;
              icon.alt = `Goal ${goalNumber} Reached`;
            }
          }
        }
      });

      if (goalMessageElement) {
        if (nextGoalIndex === -1) {
          const message = postGoalMessages[postGoalMessages.length - 1];
          goalMessageElement.innerHTML = message;
        } else {
          const remainingForGoal = thresholds[nextGoalIndex] - cartTotalCents;
          const remainingAmount = remainingForGoal / 100;
          const remainingAmountFormatted = this.formatCurrency(
            currencyFormat,
            remainingAmount
          );
          const preGoalMessageTemplate = preGoalMessages[nextGoalIndex];
          const message = preGoalMessageTemplate.replace(
            "[x]",
            remainingAmountFormatted
          );
          goalMessageElement.innerHTML = message;
        }
      }
    }
  }

  formatCurrency(currencyFormat, amount) {
    let formattedAmount = "";
    formattedAmount = currencyFormat
      .replace("{{amount}}", amount.toFixed(2))
      .replace("{{amount_no_decimals}}", amount.toFixed(0))
      .replace(
        "{{amount_with_comma_separator}}",
        amount.toFixed(2).replace(".", ",")
      )
      .replace(
        "{{amount_no_decimals_with_comma_separator}}",
        amount.toFixed(0).replace(".", ",")
      )
      .replace(
        "{{amount_with_apostrophe_separator}}",
        amount.toFixed(2).replace(".", "'")
      )
      .replace(
        "{{amount_no_decimals_with_space_separator}}",
        amount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      )
      .replace(
        "{{amount_with_space_separator}}",
        amount
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
          .replace(".", ",")
      )
      .replace(
        "{{amount_with_period_and_space_separator}}",
        amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      );
    return formattedAmount;
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : "";
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    if (document.activeElement) document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}

customElements.define("cart-items", CartItems);

/* ==========================================
   Auto-Bonus: resilient add-on-trigger (qty=1), user-removable
   Place at END of cart.js
   ========================================== */
(function () {
  // -------- CONFIG (edit these) --------
  const CONFIG = {
    enabled: false,

    // Triggers: list variant IDs or handles (all variants of those products)
    triggerVariantIds: [
      // '1234567890',
    ],
    triggerHandles: [
      // 'tabby-shoulder-26',
      // 'teri-shoulder'
    ],

    // Bonus: choose ONE method
    bonusVariantId: null,              // e.g. '2222222222' if bonus product has multiple variants
    bonusHandle: 'cherry-bag-charm',   // use when bonus has a single default variant

    bonusQuantity: 1,
    LOG: false
  };

  // -------- INTERNALS --------
  const triggerSet = new Set((CONFIG.triggerVariantIds || []).map(String));
  let resolving = false;
  let reconciling = false;

  // Manual-removal guard: do not re-add until a NEW trigger add is detected
  const LS_KEY = 'auto_bonus_removed_until_new_trigger';
  function getRemovedGuard() { return localStorage.getItem(LS_KEY) === '1'; }
  function setRemovedGuard(v) { localStorage.setItem(LS_KEY, v ? '1' : ''); }

  // When *no* triggers remain, clear the guard; when a *new* trigger is added, clear the guard.
  let lastSnapshot = { trigger: false, bonus: false };

  const log = (...a) => { if (CONFIG.LOG) console.log('[AutoBonus]', ...a); };
  const warn = (...a) => { if (CONFIG.LOG) console.warn('[AutoBonus]', ...a); };
  const err = (...a) => console.error('[AutoBonus]', ...a);

  // Header bubble updater (mirrors CartItems.updateCartIconBubble) with cache-bust
  async function updateHeaderBubble() {
    try {
      const res = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble&ts=${Date.now()}`, {
        headers: { "Cache-Control": "no-store" },
      });
      const html = new DOMParser().parseFromString(await res.text(), "text/html");
      const target = document.getElementById("cart-icon-bubble");
      const source = html.getElementById("cart-icon-bubble");
      if (target && source) target.replaceWith(source);
    } catch (e) {
      console.error(e);
    } finally {
      window.__updateCartCountsFromCart();
    }
  }

  // Instantly refresh visible cart UI (drawer or main) without waiting for pubsub
  async function refreshCartUI() {
    try {
      if (document.querySelector("cart-drawer")) {
        const res = await fetch(`${routes.cart_url}?section_id=cart-drawer&ts=${Date.now()}`, {
          headers: { "Cache-Control": "no-store" },
        });
        const html = new DOMParser().parseFromString(await res.text(), "text/html");
        const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) targetElement.replaceWith(sourceElement);
        }
      } else {
        const res = await fetch(`${routes.cart_url}?section_id=main-cart-items&ts=${Date.now()}`, {
          headers: { "Cache-Control": "no-store" },
        });
        const html = new DOMParser().parseFromString(await res.text(), "text/html");
        const sourceQty = html.querySelector("cart-items");
        const targetQty = document.querySelector("cart-items");
        if (sourceQty && targetQty) targetQty.innerHTML = sourceQty.innerHTML;
      }
      await updateHeaderBubble(); // bubble HTML
      await window.__updateCartCountsFromCart(); // and raw counts
    } catch (e) {
      console.error(e);
    }
  }

  // -------- HELPERS --------
  async function fetchJSON(url, opts) {
    const r = await fetch(url, opts);
    if (!r.ok) throw new Error(`HTTP ${r.status} on ${url}`);
    return r.json();
  }
  const getCart = () => fetchJSON(`${routes.cart_url}.js?ts=${Date.now()}`, { headers: { Accept: 'application/json' } });
  const productByHandle = (h) => fetchJSON(`/products/${h}.js?ts=${Date.now()}`, { headers: { Accept: 'application/json' } });

  async function resolveTriggersIfNeeded() {
    if (resolving) return;
    resolving = true;
    try {
      const handles = CONFIG.triggerHandles || [];
      if (handles.length) {
        const products = await Promise.all(handles.map(productByHandle));
        products.forEach(p => (p.variants || []).forEach(v => triggerSet.add(String(v.id))));
      }
      log('Resolved trigger variants:', Array.from(triggerSet));
    } catch (e) {
      warn('Failed to resolve trigger handles:', e);
    } finally {
      resolving = false;
    }
  }

  async function getBonusVariantId() {
    if (CONFIG.bonusVariantId) return String(CONFIG.bonusVariantId);
    if (!CONFIG.bonusHandle) return null;
    try {
      const p = await productByHandle(CONFIG.bonusHandle);
      const id = p?.variants?.[0]?.id ? String(p.variants[0].id) : null;
      log('Resolved bonus variant:', id);
      return id;
    } catch (e) {
      err('Failed to resolve bonus by handle:', e);
      return null;
    }
  }

  function cartHasAnyTrigger(cart) {
    return cart.items.some(it => triggerSet.has(String(it.id)));
  }
  function cartHasBonus(cart, bonusVid) {
    return cart.items.some(it => String(it.id) === String(bonusVid));
  }
  function findBonusLineKey(cart, bonusVid) {
    const line = cart.items.find(it => String(it.id) === String(bonusVid));
    return line?.key || null;
  }

  // ✅ Use /cart/add.js to avoid navigation
  async function addBonusIfMissing({ openDrawer = true } = {}) {
    const bonusVid = await getBonusVariantId();
    if (!bonusVid) return false;

    const cart = await getCart();
    if (cartHasBonus(cart, bonusVid)) {
      log('bonus already present; skip');
      return false;
    }

    log('Adding bonus', bonusVid);
    const resp = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: bonusVid, quantity: CONFIG.bonusQuantity || 1 })
    });
    if (!resp.ok) {
      const txt = await resp.text().catch(() => '(no body)');
      err('bonus add failed:', resp.status, resp.statusText, '|', txt);
      return false;
    }

    // Instantly update visible cart UI & header bubble + counts
    await refreshCartUI();

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-bonus', openDrawer, cartData: null });
      log('Published cartUpdate (openDrawer:', !!openDrawer, ')');
    }

    return true;
  }

  // ✅ Use /cart/change.js to avoid navigation when not requesting sections HTML
  async function removeBonusIfPresent() {
    const bonusVid = await getBonusVariantId();
    if (!bonusVid) return false;
    const cart = await getCart();
    const key = findBonusLineKey(cart, bonusVid);
    if (!key) return false;

    log('Removing bonus (no triggers remain)');
    const resp = await fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    if (!resp.ok) {
      const txt = await resp.text().catch(() => '(no body)');
      err('bonus remove failed:', resp.status, resp.statusText, '|', txt);
      return false;
    }

    // Instantly update visible cart UI & header bubble + counts
    await refreshCartUI();

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-bonus', openDrawer: false, cartData: null });
      log('Published cartUpdate (openDrawer: false)');
    }
    return true;
  }

  // -------- CORE: always reconcile from cart state --------
  async function reconcile(reason, { openDrawerAfterAdd = false } = {}) {
    if (!CONFIG.enabled) {
      await updateHeaderBubble();
      await window.__updateCartCountsFromCart();
      return;
    }
    if (reconciling) return;
    reconciling = true;
    try {
      await resolveTriggersIfNeeded();
      const [cart, bonusVid] = await Promise.all([getCart(), getBonusVariantId()]);
      if (!bonusVid) { warn('No bonus variant id'); return; }

      const trigger = cartHasAnyTrigger(cart);
      const bonus   = cartHasBonus(cart, bonusVid);

      // Reset guard if no triggers at all
      if (!trigger && getRemovedGuard()) {
        setRemovedGuard(false);
      }

      // Detect manual removal
      if (trigger && lastSnapshot.trigger && lastSnapshot.bonus && !bonus) {
        setRemovedGuard(true);
        log('Detected manual removal; set removed guard (pre-add)');
      }

      // Respect short-lived suppression during user-initiated upsell toggle
      if (window.__UPS_SESS_SUPPRESS_AUTOBONUS_ADD) {
        log('Suppressed auto-bonus add due to user upsell toggle in-flight');
      } else {
        if (trigger && !bonus && !getRemovedGuard()) {
          const added = await addBonusIfMissing({ openDrawerAfterAdd });
          if (added) await refreshCartUI();
        }
      }

      if (!trigger && bonus) {
        const removed = await removeBonusIfPresent();
        if (removed) await refreshCartUI();
      }

      lastSnapshot = { trigger, bonus };
      log('Reconcile', { reason, trigger, bonus, removedGuard: getRemovedGuard() });

      // extra safety: ensure bubble + count sync
      await updateHeaderBubble();
      await window.__updateCartCountsFromCart();
    } catch (e) {
      warn('reconcile error', e);
    } finally {
      reconciling = false;
    }
  }

  // -------- WIRING --------

  // A) Pub/Sub
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (evt) => {
      if (evt && evt.source === 'auto-bonus') return; // avoid loop
      reconcile('pubsub'); // immediate (no debounce) for instant UI
    });
    log('Subscribed to PUB_SUB_EVENTS.cartUpdate');
  } else {
    log('Pub/Sub not available; still works via network + startup hooks');
  }

  // B) Network hooks: catch *any* /cart/add (various body shapes) and /cart/change/update
  const _fetch = window.fetch.bind(window);
  window.fetch = async function (input, init = {}) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const method = (init && init.method ? init.method : (typeof input !== 'string' && input && input.method) || 'GET').toUpperCase();
    const isAdd = /\/cart\/add(\.js)?/.test(url) && method === 'POST';
    const isChange = /\/cart\/(change|update)\.js/.test(url) && method === 'POST';

    // Pre-parse variant id(s) if we can (handles many shapes)
    let addedTriggerDetected = false;
    if (isAdd) {
      try {
        const body = init && init.body;
        const headers = init && init.headers;
        const ctype = (headers && (headers['Content-Type'] || headers['content-type'])) || '';

        const collectIds = [];
        if (body instanceof FormData) {
          const id = body.get('id');
          if (id) collectIds.push(String(id));
          for (const [k, v] of body.entries()) {
            if (/^items\[\d+\]\[id\]$/.test(k)) collectIds.push(String(v));
          }
        } else if (typeof body === 'string') {
          if ((ctype || '').toLowerCase().includes('application/json')) {
            const payload = JSON.parse(body);
            if (payload?.id) collectIds.push(String(payload.id));
            if (Array.isArray(payload?.items)) {
              payload.items.forEach(it => { if (it?.id) collectIds.push(String(it.id)); });
            }
          } else {
            const params = new URLSearchParams(body);
            const id = params.get('id');
            if (id) collectIds.push(String(id));
            for (const [k, v] of params.entries()) {
              if (/^items\[\d+\]\[id\]$/.test(k)) collectIds.push(String(v));
            }
          }
        }

        if (collectIds.some(vid => triggerSet.has(String(vid)))) {
          setRemovedGuard(false); // NEW trigger add -> allow re-adding again
          addedTriggerDetected = true;
          log('Detected trigger add via fetch:', collectIds);
        }
      } catch (_) {}
    }

    const resp = await _fetch(input, init);

    if (isAdd || isChange) {
      // Reconcile immediately for instant auto-bonus and bubble updates
      reconcile(isAdd ? 'fetch-add' : 'fetch-change', { openDrawerAfterAdd: !!addedTriggerDetected });
    }
    return resp;
  };

  // C) XHR fallback (older themes/apps)
  (function () {
    const OrigXHR = window.XMLHttpRequest;
    function WrappedXHR() {
      const xhr = new OrigXHR();
      let _method = 'GET', _url = '', _body = null;
      const _open = xhr.open, _send = xhr.send;
      xhr.open = function (m, u) { _method = (m || 'GET').toUpperCase(); _url = u || ''; return _open.apply(xhr, arguments); };
      xhr.send = function (b) {
        _body = b;
        xhr.addEventListener('load', function () {
          const isAdd = /\/cart\/add(\.js)?/.test(_url) && _method === 'POST';
          const isChange = /\/cart\/(change|update)\.js/.test(_url) && _method === 'POST';
          if (isAdd) {
            try {
              if (typeof _body === 'string') {
                try {
                  const j = JSON.parse(_body);
                  const ids = [
                    j?.id && String(j.id),
                    ...(Array.isArray(j?.items) ? j.items.map(it => String(it.id || '')) : [])
                  ].filter(Boolean);
                  if (ids.some(vid => triggerSet.has(String(vid)))) setRemovedGuard(false);
                } catch {
                  const params = new URLSearchParams(_body);
                  const vals = [];
                  if (params.get('id')) vals.push(String(params.get('id')));
                  for (const [k, v] of params.entries()) if (/^items\[\d+\]\[id\]$/.test(k)) vals.push(String(v));
                  if (vals.some(vid => triggerSet.has(String(vid)))) setRemovedGuard(false);
                }
              }
            } catch (_) {}
          }
          if (isAdd || isChange) reconcile(isAdd ? 'xhr-add' : 'xhr-change', { openDrawerAfterAdd: isAdd });
        });
        return _send.apply(xhr, arguments);
      };
      return xhr;
    }
    window.XMLHttpRequest = WrappedXHR;
  })();

  // D) Form submit fallback (PDPs posting real forms)
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add/.test(action)) return;

    const idInput = form.querySelector('input[name="id"]');
    const vid = idInput ? String(idInput.value || '') : '';
    if (vid && triggerSet.has(vid)) {
      setRemovedGuard(false); // new trigger add
    }
    // Reconcile shortly after form submit completes
    setTimeout(() => reconcile('form-add', { openDrawerAfterAdd: !!(vid && triggerSet.has(vid)) }), 150);
  }, true);

  // E) Startup passes
  (async function boot() {
    try {
      await resolveTriggersIfNeeded();
      await getBonusVariantId().then(id => log('Resolved bonus variant:', id));
      reconcile('page-load', { openDrawerAfterAdd: false });
      setTimeout(() => reconcile('page-load-late', { openDrawerAfterAdd: false }), 150);
    } catch (e) {
      err('boot error', e);
    }
  })();
})();

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
